import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { ColleagueDialog } from "../../../components/utils/ColleagueDialog";
import {
  BookOpenIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  getHseDocuments,
  getHseDocument,
  acknowledgeDocumentVersionRead,
} from "../../../api/hse/documents";
import { toast } from "react-hot-toast";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { HtmlTextFormatter } from "../../../components/Utils";
import { fetchUsers } from "../../../api/user";
import { fetchHseEvents } from "../../../api/hse/events";
import { fetchOrgCharts } from "../../../api/quality/orgCharts";
import { EmployeeToolsDialog } from "../../../components/dashboard/tools/EmployeeToolsDialog";

/** Build ordered chapter trees from flat buckets { chapter, documents } so nested chapters still render when the parent has no direct documents. */
function buildChapterForestFromBuckets(buckets) {
  if (!buckets?.length) return [];
  const byId = new Map();
  buckets.forEach(({ chapter, documents }) => {
    if (!chapter?.id) return;
    byId.set(chapter.id, {
      ...chapter,
      documents: documents || [],
      children: [],
    });
  });
  const roots = [];
  byId.forEach((node) => {
    const pid = node.parent_chapter_id;
    if (pid && byId.has(pid)) {
      byId.get(pid).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRecursive = (nodes) => {
    nodes.sort((a, b) => (parseFloat(a.number) || 0) - (parseFloat(b.number) || 0));
    nodes.forEach((n) => n.children?.length && sortRecursive(n.children));
  };
  sortRecursive(roots);
  return roots;
}

function categoryExpansionKey(category, disciplineId) {
  if (disciplineId != null && disciplineId !== undefined) {
    return `category_d${disciplineId}_c${category.id}`;
  }
  return `category_global_c${category.id}`;
}

export const EmployeeHandbookView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("overview");
  const [documents, setDocuments] = useState({}); // Legacy structure by chapter
  const [documentsByStructure, setDocumentsByStructure] = useState({}); // New structure: discipline -> category -> chapter
  const [documentsWithoutChapter, setDocumentsWithoutChapter] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]); // Flat list for search
  const [chapters, setChapters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [hseEvents, setHseEvents] = useState([]);
  const [orgCharts, setOrgCharts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [colleagueDialogOpen, setColleagueDialogOpen] = useState(false);
  const [selectedColleague, setSelectedColleague] = useState(null);
  const [expandedChapters, setExpandedChapters] = useState(new Set());
  const [orgChartDialogOpen, setOrgChartDialogOpen] = useState(false);
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [ackSubmitting, setAckSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);


  // Auto-expand matching sections when searching
  useEffect(() => {
    if (searchQuery.trim() && Object.keys(documentsByStructure).length > 0) {
      const newExpanded = new Set();
      const query = searchQuery.toLowerCase();
      
      // Helper function to check if document matches search
      const documentMatches = (doc) => {
        return doc.title?.toLowerCase().includes(query) ||
               doc.description?.toLowerCase().includes(query) ||
               doc.document_type_display?.toLowerCase().includes(query) ||
               doc.category_display?.toLowerCase().includes(query) ||
               doc.chapter?.title?.toLowerCase().includes(query) ||
               doc.document_number?.toLowerCase().includes(query) ||
               doc.discipline?.name?.toLowerCase().includes(query) ||
               doc.category?.name?.toLowerCase().includes(query);
      };
      
      // Expand disciplines that have matching documents
      Object.values(documentsByStructure.disciplines || {}).forEach(disciplineData => {
        const discipline = disciplineData.discipline;
        let hasMatches = false;
        
        // Check categories
        Object.values(disciplineData.categories || {}).forEach(categoryData => {
          const category = categoryData.category;
          let categoryHasMatches = false;
          
          // Check chapters
          Object.values(categoryData.chapters || {}).forEach(chapterData => {
            const chapter = chapterData.chapter;
            const chapterMatches = chapter?.title?.toLowerCase().includes(query) ||
              chapterData.documents?.some(documentMatches);
            
            if (chapterMatches) {
              categoryHasMatches = true;
              hasMatches = true;
              newExpanded.add(`discipline_${discipline.id}`);
              newExpanded.add(categoryExpansionKey(category, discipline.id));
              newExpanded.add(chapter.id);
              // Expand parent if sub-chapter
              if (chapter.parent_chapter_id) {
                newExpanded.add(chapter.parent_chapter_id);
              }
            }
          });
          
          // Check documents without chapter
          if (categoryData.withoutChapter?.some(documentMatches)) {
            categoryHasMatches = true;
            hasMatches = true;
            newExpanded.add(`discipline_${discipline.id}`);
            newExpanded.add(categoryExpansionKey(category, discipline.id));
          }
        });
        
        // Check chapters without category
        if (disciplineData.withoutCategory) {
          Object.values(disciplineData.withoutCategory.chapters || {}).forEach(chapterData => {
            const chapter = chapterData.chapter;
            const chapterMatches = chapter?.title?.toLowerCase().includes(query) ||
              chapterData.documents?.some(documentMatches);
            
            if (chapterMatches) {
              hasMatches = true;
              newExpanded.add(`discipline_${discipline.id}`);
              newExpanded.add(chapter.id);
              if (chapter.parent_chapter_id) {
                newExpanded.add(chapter.parent_chapter_id);
              }
            }
          });
          
          if (disciplineData.withoutCategory.withoutChapter?.some(documentMatches)) {
            hasMatches = true;
            newExpanded.add(`discipline_${discipline.id}`);
          }
        }
      });
      
      // Expand categories without discipline that match
      Object.values(documentsByStructure.withoutDiscipline?.categories || {}).forEach(categoryData => {
        const category = categoryData.category;
        let categoryHasMatches = false;
        
        Object.values(categoryData.chapters || {}).forEach(chapterData => {
          const chapter = chapterData.chapter;
          const chapterMatches = chapter?.title?.toLowerCase().includes(query) ||
            chapterData.documents?.some(documentMatches);
          
          if (chapterMatches) {
            categoryHasMatches = true;
            newExpanded.add(categoryExpansionKey(category, null));
            newExpanded.add(chapter.id);
            if (chapter.parent_chapter_id) {
              newExpanded.add(chapter.parent_chapter_id);
            }
          }
        });
        
        if (categoryData.withoutChapter?.some(documentMatches)) {
          categoryHasMatches = true;
          newExpanded.add(categoryExpansionKey(category, null));
        }
      });
      
      // Expand chapters without discipline and category
      Object.values(documentsByStructure.withoutDiscipline?.withoutCategory?.chapters || {}).forEach(chapterData => {
        const chapter = chapterData.chapter;
        const chapterMatches = chapter?.title?.toLowerCase().includes(query) ||
          chapterData.documents?.some(documentMatches);
        
        if (chapterMatches) {
          newExpanded.add(chapter.id);
          if (chapter.parent_chapter_id) {
            newExpanded.add(chapter.parent_chapter_id);
          }
        }
      });
      
      setExpandedChapters(newExpanded);
    } else if (!searchQuery.trim()) {
      // Clear expanded chapters when search is cleared
      setExpandedChapters(new Set());
    }
  }, [searchQuery, documentsByStructure]);

  // Handle URL-based tab navigation
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      const tabIndex = parseInt(tabFromUrl);
      const validTabs = [
        "overview",
        "documents",
        "employees",
        "hse_history",
      ];
      if (tabIndex >= 0 && tabIndex < validTabs.length) {
        setActiveTab(validTabs[tabIndex]);
      }
    }
  }, [searchParams]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load HSE documents for KS and HMS categories
      const [hmsDocumentsRes, ksDocumentsRes, employeesRes, hseEventsRes, orgChartsRes] =
        await Promise.all([
          getHseDocuments({ category: 'hms', status: 'approved' }),
          getHseDocuments({ category: 'ks', status: 'approved' }),
          fetchUsers({
            filters: [{ name: "EMPLOYEE", value: true }],
            active: true, // Only active employees
          }),
          fetchHseEvents({ per_page: 10 }),
          fetchOrgCharts({ per_page: 50 }),
        ]);

      // Combine HMS and KS documents
      const allDocuments = [
        ...(hmsDocumentsRes?.documents || []),
        ...(ksDocumentsRes?.documents || [])
      ];

      // Organize documents by structure: Discipline -> Category -> Chapter -> Documents
      const structure = {
        disciplines: {}, // discipline_id -> { discipline, categories: { category_id -> { category, chapters: {...} } } }
        withoutDiscipline: {
          categories: {}, // category_id -> { category, chapters: {...} }
          withoutCategory: {
            chapters: {}, // chapter_id -> { chapter, documents: [] }
            withoutChapter: []
          }
        }
      };

      // Extract unique chapters and build chapter map with hierarchy
      const chapterMap = new Map();
      const documentsByChapter = {}; // Legacy structure
      const documentsWithoutChapterList = [];

      allDocuments.forEach(doc => {
        // Build chapter map with parent relationships
        if (doc.chapter) {
          const chapterId = doc.chapter.id;
          if (!chapterMap.has(chapterId)) {
            chapterMap.set(chapterId, {
              ...doc.chapter,
              parent_chapter_id: doc.chapter.parent_chapter_id || null,
              children: []
            });
          }
          // Legacy structure
          if (!documentsByChapter[chapterId]) {
            documentsByChapter[chapterId] = {
              chapter: doc.chapter,
              documents: []
            };
          }
          documentsByChapter[chapterId].documents.push(doc);
        } else {
          documentsWithoutChapterList.push(doc);
        }

        // Build hierarchical structure
        const disciplineId = doc.discipline?.id || 'no_discipline';
        const categoryId = doc.category?.id || 'no_category';
        const chapterId = doc.chapter?.id || 'no_chapter';

        if (disciplineId !== 'no_discipline') {
          // Document has discipline
          if (!structure.disciplines[disciplineId]) {
            structure.disciplines[disciplineId] = {
              discipline: doc.discipline,
              categories: {}
            };
          }
          
          if (categoryId !== 'no_category') {
            // Document has category
            if (!structure.disciplines[disciplineId].categories[categoryId]) {
              structure.disciplines[disciplineId].categories[categoryId] = {
                category: doc.category,
                chapters: {}
              };
            }
            
            if (chapterId !== 'no_chapter') {
              // Document has chapter
              if (!structure.disciplines[disciplineId].categories[categoryId].chapters[chapterId]) {
                structure.disciplines[disciplineId].categories[categoryId].chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.disciplines[disciplineId].categories[categoryId].chapters[chapterId].documents.push(doc);
            } else {
              // Document has discipline and category but no chapter
              if (!structure.disciplines[disciplineId].categories[categoryId].withoutChapter) {
                structure.disciplines[disciplineId].categories[categoryId].withoutChapter = [];
              }
              structure.disciplines[disciplineId].categories[categoryId].withoutChapter.push(doc);
            }
          } else {
            // Document has discipline but no category
            if (!structure.disciplines[disciplineId].withoutCategory) {
              structure.disciplines[disciplineId].withoutCategory = {
                chapters: {},
                withoutChapter: []
              };
            }
            if (chapterId !== 'no_chapter') {
              if (!structure.disciplines[disciplineId].withoutCategory.chapters[chapterId]) {
                structure.disciplines[disciplineId].withoutCategory.chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.disciplines[disciplineId].withoutCategory.chapters[chapterId].documents.push(doc);
            } else {
              structure.disciplines[disciplineId].withoutCategory.withoutChapter.push(doc);
            }
          }
        } else {
          // Document has no discipline
          if (categoryId !== 'no_category') {
            // Document has category but no discipline
            if (!structure.withoutDiscipline.categories[categoryId]) {
              structure.withoutDiscipline.categories[categoryId] = {
                category: doc.category,
                chapters: {}
              };
            }
            
            if (chapterId !== 'no_chapter') {
              if (!structure.withoutDiscipline.categories[categoryId].chapters[chapterId]) {
                structure.withoutDiscipline.categories[categoryId].chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.withoutDiscipline.categories[categoryId].chapters[chapterId].documents.push(doc);
            } else {
              if (!structure.withoutDiscipline.categories[categoryId].withoutChapter) {
                structure.withoutDiscipline.categories[categoryId].withoutChapter = [];
              }
              structure.withoutDiscipline.categories[categoryId].withoutChapter.push(doc);
            }
          } else {
            // Document has no discipline and no category
            if (chapterId !== 'no_chapter') {
              if (!structure.withoutDiscipline.withoutCategory.chapters[chapterId]) {
                structure.withoutDiscipline.withoutCategory.chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structure.withoutDiscipline.withoutCategory.chapters[chapterId].documents.push(doc);
            } else {
              structure.withoutDiscipline.withoutCategory.withoutChapter.push(doc);
            }
          }
        }
      });

      // Build chapter hierarchy from chapterMap
      const chaptersArray = Array.from(chapterMap.values());
      chaptersArray.forEach(chapter => {
        if (chapter.parent_chapter_id) {
          const parent = chapterMap.get(chapter.parent_chapter_id);
          if (parent) {
            if (!parent.children) parent.children = [];
            parent.children.push(chapter);
          }
        }
      });

      // Sort chapters
      const sortChapters = (chapters) => {
        return chapters.sort((a, b) => {
          const numA = parseFloat(a.number) || 0;
          const numB = parseFloat(b.number) || 0;
          return numA - numB;
        }).map(ch => ({
          ...ch,
          children: ch.children ? sortChapters(ch.children) : []
        }));
      };

      const rootChapters = chaptersArray.filter(ch => !ch.parent_chapter_id);
      const sortedChapters = sortChapters(rootChapters);

      setChapters(sortedChapters);
      setDocuments(documentsByChapter); // Keep for legacy compatibility
      setDocumentsByStructure(structure); // New hierarchical structure
      setDocumentsWithoutChapter(documentsWithoutChapterList);
      // Store flat list for search
      setAllDocuments(allDocuments);

      // Handle employees data - filter to only active employees with employment
      const employeesData = employeesRes?.data || [];
      const activeEmployees = Array.isArray(employeesData) 
        ? employeesData.filter(emp => emp.active && emp.employment) 
        : [];
      setEmployees(activeEmployees);

      // Handle HSE events data
      const hseEventsData = hseEventsRes?.data || [];
      setHseEvents(Array.isArray(hseEventsData) ? hseEventsData : []);

      // Handle org charts data - only show approved ones
      const orgChartsData = orgChartsRes?.data || [];
      const approvedCharts = Array.isArray(orgChartsData) 
        ? orgChartsData.filter(chart => chart.approved_revision) 
        : [];
      setOrgCharts(approvedCharts);
    } catch (err) {
      console.error("Error loading employee handbook data:", err);
      setError("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentClick = async (document) => {
    setSelectedDocument(document);
    setDocumentDialogOpen(true);
    setLoadingDocument(true);
    
    try {
      // Fetch full document content
      const response = await getHseDocument(document.id);
      setDocumentContent(response?.document);
    } catch (err) {
      console.error("Error loading document:", err);
      setError("Failed to load document content");
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleAcknowledgeRead = async () => {
    if (!selectedDocument?.id || !documentContent?.approved_version_id) return;
    setAckSubmitting(true);
    try {
      await acknowledgeDocumentVersionRead(
        selectedDocument.id,
        documentContent.approved_version_id,
        null
      );
      toast.success(t("hse.document.acknowledge_read_success"));
      await loadData();
      setDocumentContent((prev) =>
        prev
          ? {
              ...prev,
              current_user_read_status: {
                ...(prev.current_user_read_status || {}),
                exempt_as_creator: prev.current_user_read_status?.exempt_as_creator,
                acknowledged_read_understood: true,
                read_understood_at: new Date().toISOString(),
              },
            }
          : prev
      );
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] || t("hse.document.acknowledge_read_error");
      toast.error(msg);
    } finally {
      setAckSubmitting(false);
    }
  };

  const handleEmployeeClick = (employee) => {
    setSelectedColleague(employee);
    setColleagueDialogOpen(true);
  };

  const getHseEventTypeIcon = (eventType) => {
    switch (eventType) {
      case "injury":
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case "near_miss":
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case "property_damage":
        return <XCircleIcon className="h-5 w-5 text-orange-500" />;
      case "environmental":
        return <ExclamationTriangleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getHseEventStatusBadge = (status) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case "draft":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {t("Draft")}
          </span>
        );
      case "submitted":
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            {t("Submitted")}
          </span>
        );
      case "under_review":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            {t("Under Review")}
          </span>
        );
      case "closed":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            {t("Closed")}
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const toggleChapterExpansion = (chapterId) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(chapterId)) {
      newExpanded.delete(chapterId);
    } else {
      newExpanded.add(chapterId);
    }
    setExpandedChapters(newExpanded);
  };

  // Function to organize chapters in hierarchical structure
  const organizeChaptersHierarchy = (chapters) => {
    if (!chapters || !Array.isArray(chapters)) return [];
    
    const chapterMap = new Map();
    const rootChapters = [];
    
    // First pass: create map and identify root chapters
    chapters.forEach(chapter => {
      chapterMap.set(chapter.id, { ...chapter, children: chapter.children || [] });
      if (!chapter.parent_chapter_id) {
        rootChapters.push(chapterMap.get(chapter.id));
      }
    });
    
    // Second pass: build hierarchy if parent_chapter_id is available
    chapters.forEach(chapter => {
      if (chapter.parent_chapter_id) {
        const parent = chapterMap.get(chapter.parent_chapter_id);
        if (parent) {
          parent.children.push(chapterMap.get(chapter.id));
        } else {
          // Parent not found, treat as root chapter
          if (!rootChapters.find(c => c.id === chapter.id)) {
            rootChapters.push(chapterMap.get(chapter.id));
          }
        }
      }
    });
    
    // If no hierarchy found, return all chapters sorted by number
    if (rootChapters.length === 0) {
      return chapters.sort((a, b) => {
        const numA = parseFloat(a.number) || 0;
        const numB = parseFloat(b.number) || 0;
        return numA - numB;
      });
    }
    
    // Sort root chapters by number
    rootChapters.sort((a, b) => {
      const numA = parseFloat(a.number) || 0;
      const numB = parseFloat(b.number) || 0;
      return numA - numB;
    });
    
    return rootChapters;
  };

  const handbookChapterCount = useMemo(() => {
    const ids = new Set();
    allDocuments.forEach((d) => {
      if (d.chapter?.id != null) ids.add(d.chapter.id);
    });
    return ids.size;
  }, [allDocuments]);

  const tabs = [
    { id: "overview", label: t("Overview"), icon: BookOpenIcon },
    { id: "documents", label: t("KS/HMS Documents"), icon: DocumentTextIcon },
    { id: "employees", label: t("Colleagues"), icon: UserGroupIcon },
    {
      id: "hse_history",
      label: t("HSE Historikk"),
      icon: ExclamationTriangleIcon,
    },
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
    if (tabIndex !== -1) {
      setSearchParams({ tab: tabIndex.toString() });
    }
  };

  const pendingAckCount = useMemo(
    () =>
      allDocuments.filter((d) => !d.current_user_read_status?.acknowledged_read_understood)
        .length,
    [allDocuments]
  );

  const DocReadStatusBadge = ({ doc }) => {
    const st = doc?.current_user_read_status;
    if (st?.exempt_as_creator) {
      return (
        <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
          {t("employee_handbook.read_status_creator_exempt")}
        </span>
      );
    }
    if (st?.acknowledged_read_understood) {
      return (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
          <CheckCircleIcon className="h-3.5 w-3.5" />
          {t("employee_handbook.read_status_acknowledged")}
        </span>
      );
    }
    return (
      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900">
        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
        {t("employee_handbook.read_status_pending")}
      </span>
    );
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {pendingAckCount > 0 ? (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-600" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-950">
              {t("employee_handbook.pending_read_ack_title")}
            </p>
            <p className="mt-1 text-sm text-amber-900">
              {t("employee_handbook.pending_read_ack_body").replace(
                "{count}",
                String(pendingAckCount)
              )}
            </p>
            <button
              type="button"
              onClick={() => handleTabChange("documents")}
              className="mt-2 text-sm font-semibold text-amber-950 underline decoration-amber-800/40 hover:decoration-amber-950"
            >
              {t("employee_handbook.go_to_documents_tab")}
            </button>
          </div>
        </div>
      ) : allDocuments.length > 0 ? (
        <div className="flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-950">
            {t("employee_handbook.all_documents_acknowledged")}
          </p>
        </div>
      ) : null}
      {/* Quick Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {t("KS/HMS Documents")}
            </span>
          </div>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {allDocuments.length}
          </p>
          {pendingAckCount > 0 ? (
            <p className="mt-1 text-xs font-semibold text-amber-800">
              {t("employee_handbook.stat_line_pending").replace(
                "{count}",
                String(pendingAckCount)
              )}
            </p>
          ) : null}
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <BookOpenIcon className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">
              {t("Kapitler")}
            </span>
          </div>
          <p className="text-2xl font-bold text-green-900 mt-1">
            {handbookChapterCount}
          </p>
        </div>

        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium text-red-900">
              {t("Hendelser")}
            </span>
          </div>
          <p className="text-2xl font-bold text-red-900 mt-1">
            {hseEvents.length}
          </p>
        </div>

        <div
          onClick={() => setOrgChartDialogOpen(true)}
          className="bg-purple-50 rounded-lg p-4 hover:bg-purple-100 transition-colors cursor-pointer"
        >
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">
              {t("Organisasjonskart")}
            </span>
          </div>
          <p className="text-sm text-purple-700 mt-1">
            {orgCharts.length === 0 
              ? t("Ingen godkjente kart") 
              : `${orgCharts.length} ${orgCharts.length === 1 ? t("kart") : t("kart")}`}
          </p>
        </div>

        <div
          onClick={() => setToolsDialogOpen(true)}
          className="bg-slate-50 rounded-lg p-4 hover:bg-slate-100 transition-colors cursor-pointer"
        >
          <div className="flex items-center space-x-2">
            <WrenchScrewdriverIcon className="h-5 w-5 text-slate-600" />
            <span className="text-sm font-medium text-slate-900">
              {t("tools")}
            </span>
          </div>
          <p className="text-sm text-slate-700 mt-1">
            {t("tool.employee_tools_short")}
          </p>
        </div>
      </div>

      {/* Recent Documents */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("Recent KS/HMS Documents")}
        </h3>
        <div className="space-y-3">
          {(() => {
            const recentDocuments = [...allDocuments]
              .sort((a, b) => {
                const tb = new Date(b.updated_at || b.updatedAt || 0).getTime();
                const ta = new Date(a.updated_at || a.updatedAt || 0).getTime();
                return tb - ta;
              })
              .slice(0, 6);

            return recentDocuments.map((document) => (
              <div
                key={document.id}
                onClick={() => handleDocumentClick(document)}
                className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors duration-200"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center space-x-3">
                    <DocumentTextIcon className="h-5 w-5 flex-shrink-0 text-blue-600" />
                    <div className="min-w-0">
                      <h4 className="font-medium text-gray-900">
                        {document.title}
                      </h4>
                      <div className="mt-1 flex flex-wrap items-center space-x-2">
                        <p className="text-sm text-gray-500">
                          {document.chapter?.title || t("Uten kapittel")}
                        </p>
                        {document.document_type_display && (
                          <span className="text-xs text-gray-400">
                            • {document.document_type_display}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <DocReadStatusBadge doc={document} />
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("Nylige hendelser")}
        </h3>
        <div className="space-y-3">
          {hseEvents.slice(0, 3).map((event) => (
            <Link
              key={event.id}
              to={`/dashboard/hse/events/${event.id}`}
              className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getHseEventTypeIcon(event.event_type)}
                  <div>
                    <h4 className="font-medium text-gray-900">{event.title}</h4>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {new Date(event.event_date).toLocaleDateString()}
                      </span>
                      {getHseEventStatusBadge(event.status)}
                    </div>
                  </div>
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSubChapterDocuments = (subChapter, filteredDocsByChapter, level = 1) => {
    const subChapterDocuments = filteredDocsByChapter[subChapter.id]?.documents || [];
    if (subChapterDocuments.length === 0 && (!subChapter.children || subChapter.children.length === 0)) return null;
    
    return (
      <div key={subChapter.id} className="ml-4 border-l-2 border-gray-300 pl-3">
        <div
          className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 transition-colors duration-200 rounded-md px-2 -ml-2"
          onClick={() => toggleChapterExpansion(subChapter.id)}
        >
          <div className="flex items-center space-x-2">
            <span className="text-xs font-medium text-gray-500">#{subChapter.number}</span>
            <span className="text-sm text-gray-700 font-medium">{subChapter.title}</span>
            {subChapterDocuments.length > 0 && (
              <span className="text-xs text-gray-400">
                ({subChapterDocuments.length} {subChapterDocuments.length === 1 ? 'dokument' : 'dokumenter'})
              </span>
            )}
          </div>
          {expandedChapters.has(subChapter.id) ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>

        {expandedChapters.has(subChapter.id) && (
          <div className="mt-2 space-y-1">
            {subChapterDocuments.map((document) => (
              <div
                key={document.id}
                onClick={() => handleDocumentClick(document)}
                className="p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-200 border-b border-gray-100 last:border-b-0 rounded-md"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center space-x-2">
                    <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-blue-600" />
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">
                        {document.title}
                      </h4>
                      {document.document_type_display && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {document.document_type_display}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <DocReadStatusBadge doc={document} />
                    <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
            
            {/* Recursively render nested sub-chapters */}
            {subChapter.children && subChapter.children.length > 0 && (
              <div className="mt-2">
                {subChapter.children.map((nestedSubChapter) => 
                  renderSubChapterDocuments(nestedSubChapter, filteredDocsByChapter, level + 1)
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Filter documents based on search query
  // Also includes documents whose chapter/category/discipline matches
  const filterDocuments = (docs) => {
    if (!searchQuery.trim()) return docs;
    
    const query = searchQuery.toLowerCase();
    return docs.filter(doc => {
      // Direct document match
      const documentMatches = 
        doc.title?.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.document_type_display?.toLowerCase().includes(query) ||
        doc.document_number?.toLowerCase().includes(query);
      
      // Structural match (chapter/category/discipline)
      const structureMatches = 
        doc.chapter?.title?.toLowerCase().includes(query) ||
        doc.category_display?.toLowerCase().includes(query) ||
        doc.category?.name?.toLowerCase().includes(query) ||
        doc.discipline?.name?.toLowerCase().includes(query);
      
      return documentMatches || structureMatches;
    });
  };

  // Helper function to count documents in a structure section
  const countDocumentsInStructure = (structure) => {
    let count = 0;
    
    // Count in disciplines
    Object.values(structure.disciplines || {}).forEach(discipline => {
      Object.values(discipline.categories || {}).forEach(category => {
        Object.values(category.chapters || {}).forEach(chapter => {
          count += chapter.documents?.length || 0;
        });
        count += category.withoutChapter?.length || 0;
      });
      if (discipline.withoutCategory) {
        Object.values(discipline.withoutCategory.chapters || {}).forEach(chapter => {
          count += chapter.documents?.length || 0;
        });
        count += discipline.withoutCategory.withoutChapter?.length || 0;
      }
    });
    
    // Count in withoutDiscipline
    Object.values(structure.withoutDiscipline?.categories || {}).forEach(category => {
      Object.values(category.chapters || {}).forEach(chapter => {
        count += chapter.documents?.length || 0;
      });
      count += category.withoutChapter?.length || 0;
    });
    Object.values(structure.withoutDiscipline?.withoutCategory?.chapters || {}).forEach(chapter => {
      count += chapter.documents?.length || 0;
    });
    count += structure.withoutDiscipline?.withoutCategory?.withoutChapter?.length || 0;
    
    return count;
  };

  // Render chapter with documents (recursive for sub-chapters)
  const renderChapterWithDocuments = (chapter, chapterDocs, filteredDocsByChapter, level = 0) => {
    const hasSubChapters = chapter.children && chapter.children.length > 0;
    const hasContent = chapterDocs.length > 0 || hasSubChapters;
    
    if (!hasContent) return null;
    
    return (
      <div key={chapter.id} className={level > 0 ? "ml-4 border-l-2 border-gray-300 pl-3 mt-2" : ""}>
        <div
          className={`flex items-center justify-between ${level === 0 ? 'p-4' : 'py-2 px-2'} cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${level === 0 ? '' : 'rounded-md -ml-2'}`}
          onClick={() => toggleChapterExpansion(chapter.id)}
        >
          <div className="flex items-center space-x-2">
            <span className={`${level === 0 ? 'text-base' : 'text-xs'} font-medium text-gray-500`}>
              #{chapter.number}
            </span>
            <span className={`${level === 0 ? 'text-base' : 'text-sm'} text-gray-700 font-medium`}>
              {chapter.title}
            </span>
            {chapterDocs.length > 0 && (
              <span className="text-xs text-gray-400">
                ({chapterDocs.length} {chapterDocs.length === 1 ? 'dokument' : 'dokumenter'})
              </span>
            )}
          </div>
          {expandedChapters.has(chapter.id) ? (
            <ChevronUpIcon className={`${level === 0 ? 'h-5 w-5' : 'h-4 w-4'} text-gray-400`} />
          ) : (
            <ChevronDownIcon className={`${level === 0 ? 'h-5 w-5' : 'h-4 w-4'} text-gray-400`} />
          )}
        </div>

        {expandedChapters.has(chapter.id) && (
          <div className={level === 0 ? "border-t border-gray-200" : "mt-2 space-y-1"}>
            {/* Render documents */}
            {chapterDocs.map((document) => (
              <div
                key={document.id}
                onClick={() => handleDocumentClick(document)}
                className={`${level === 0 ? 'p-4' : 'p-3'} hover:bg-gray-50 cursor-pointer transition-colors duration-200 border-b border-gray-100 last:border-b-0 ${level > 0 ? 'rounded-md' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center space-x-2">
                    <DocumentTextIcon className={`${level === 0 ? 'h-5 w-5' : 'h-4 w-4'} flex-shrink-0 text-blue-600`} />
                    <div className="min-w-0 flex-1">
                      <h4 className={`${level === 0 ? 'text-base' : 'text-sm'} font-medium text-gray-900`}>
                        {document.title}
                      </h4>
                      {document.document_type_display && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {document.document_type_display}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <DocReadStatusBadge doc={document} />
                    <ChevronRightIcon className={`${level === 0 ? 'h-4 w-4' : 'h-3 w-3'} text-gray-400`} />
                  </div>
                </div>
              </div>
            ))}
            
            {/* Render sub-chapters recursively */}
            {hasSubChapters && (
              <div className={level === 0 ? "px-4 py-3 bg-gray-50 border-t border-gray-200" : "mt-2"}>
                {level === 0 && (
                  <div className="text-xs text-gray-500 mb-2 font-medium">
                    Underkapitler
                  </div>
                )}
                <div className="space-y-2">
                  {chapter.children.map((subChapter) => {
                    const subChapterDocs = filteredDocsByChapter[subChapter.id]?.documents || [];
                    return renderChapterWithDocuments(subChapter, subChapterDocs, filteredDocsByChapter, level + 1);
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDocuments = () => {
    // Filter documents if search query exists
    let structureToRender = documentsByStructure;
    let filteredDocumentsByChapter = {};
    let filteredDocumentsWithoutChapter = [];
    
    if (searchQuery.trim()) {
      // Filter all documents
      const filtered = filterDocuments(allDocuments);
      
      // Rebuild structure with filtered documents
      structureToRender = {
        disciplines: {},
        withoutDiscipline: {
          categories: {},
          withoutCategory: {
            chapters: {},
            withoutChapter: []
          }
        }
      };
      
      filtered.forEach(doc => {
        const disciplineId = doc.discipline?.id || 'no_discipline';
        const categoryId = doc.category?.id || 'no_category';
        const chapterId = doc.chapter?.id || 'no_chapter';
        
        // Build filtered structure (same logic as loadData)
        if (disciplineId !== 'no_discipline') {
          if (!structureToRender.disciplines[disciplineId]) {
            structureToRender.disciplines[disciplineId] = {
              discipline: doc.discipline,
              categories: {}
            };
          }
          if (categoryId !== 'no_category') {
            if (!structureToRender.disciplines[disciplineId].categories[categoryId]) {
              structureToRender.disciplines[disciplineId].categories[categoryId] = {
                category: doc.category,
                chapters: {}
              };
            }
            if (chapterId !== 'no_chapter') {
              if (!structureToRender.disciplines[disciplineId].categories[categoryId].chapters[chapterId]) {
                structureToRender.disciplines[disciplineId].categories[categoryId].chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structureToRender.disciplines[disciplineId].categories[categoryId].chapters[chapterId].documents.push(doc);
            } else {
              if (!structureToRender.disciplines[disciplineId].categories[categoryId].withoutChapter) {
                structureToRender.disciplines[disciplineId].categories[categoryId].withoutChapter = [];
              }
              structureToRender.disciplines[disciplineId].categories[categoryId].withoutChapter.push(doc);
            }
          } else {
            if (!structureToRender.disciplines[disciplineId].withoutCategory) {
              structureToRender.disciplines[disciplineId].withoutCategory = { chapters: {}, withoutChapter: [] };
            }
            if (chapterId !== 'no_chapter') {
              if (!structureToRender.disciplines[disciplineId].withoutCategory.chapters[chapterId]) {
                structureToRender.disciplines[disciplineId].withoutCategory.chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structureToRender.disciplines[disciplineId].withoutCategory.chapters[chapterId].documents.push(doc);
            } else {
              structureToRender.disciplines[disciplineId].withoutCategory.withoutChapter.push(doc);
            }
          }
        } else {
          if (categoryId !== 'no_category') {
            if (!structureToRender.withoutDiscipline.categories[categoryId]) {
              structureToRender.withoutDiscipline.categories[categoryId] = {
                category: doc.category,
                chapters: {}
              };
            }
            if (chapterId !== 'no_chapter') {
              if (!structureToRender.withoutDiscipline.categories[categoryId].chapters[chapterId]) {
                structureToRender.withoutDiscipline.categories[categoryId].chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structureToRender.withoutDiscipline.categories[categoryId].chapters[chapterId].documents.push(doc);
            } else {
              if (!structureToRender.withoutDiscipline.categories[categoryId].withoutChapter) {
                structureToRender.withoutDiscipline.categories[categoryId].withoutChapter = [];
              }
              structureToRender.withoutDiscipline.categories[categoryId].withoutChapter.push(doc);
            }
          } else {
            if (chapterId !== 'no_chapter') {
              if (!structureToRender.withoutDiscipline.withoutCategory.chapters[chapterId]) {
                structureToRender.withoutDiscipline.withoutCategory.chapters[chapterId] = {
                  chapter: doc.chapter,
                  documents: []
                };
              }
              structureToRender.withoutDiscipline.withoutCategory.chapters[chapterId].documents.push(doc);
            } else {
              structureToRender.withoutDiscipline.withoutCategory.withoutChapter.push(doc);
            }
          }
        }
        
        // Also build filteredDocumentsByChapter for compatibility
        if (doc.chapter) {
          const chapterId = doc.chapter.id;
          if (!filteredDocumentsByChapter[chapterId]) {
            filteredDocumentsByChapter[chapterId] = {
              chapter: doc.chapter,
              documents: []
            };
          }
          filteredDocumentsByChapter[chapterId].documents.push(doc);
        } else {
          filteredDocumentsWithoutChapter.push(doc);
        }
      });
    } else {
      // Use original structure when no search
      // Build filteredDocumentsByChapter from documentsByStructure
      const buildChapterMap = (structure) => {
        // From disciplines
        Object.values(structure.disciplines || {}).forEach(discipline => {
          Object.values(discipline.categories || {}).forEach(category => {
            Object.values(category.chapters || {}).forEach(chapterData => {
              const chapterId = chapterData.chapter.id;
              if (!filteredDocumentsByChapter[chapterId]) {
                filteredDocumentsByChapter[chapterId] = {
                  chapter: chapterData.chapter,
                  documents: []
                };
              }
              filteredDocumentsByChapter[chapterId].documents.push(...chapterData.documents);
            });
            if (category.withoutChapter) {
              filteredDocumentsWithoutChapter.push(...category.withoutChapter);
            }
          });
          if (discipline.withoutCategory) {
            Object.values(discipline.withoutCategory.chapters || {}).forEach(chapterData => {
              const chapterId = chapterData.chapter.id;
              if (!filteredDocumentsByChapter[chapterId]) {
                filteredDocumentsByChapter[chapterId] = {
                  chapter: chapterData.chapter,
                  documents: []
                };
              }
              filteredDocumentsByChapter[chapterId].documents.push(...chapterData.documents);
            });
            if (discipline.withoutCategory.withoutChapter) {
              filteredDocumentsWithoutChapter.push(...discipline.withoutCategory.withoutChapter);
            }
          }
        });
        
        // From withoutDiscipline
        Object.values(structure.withoutDiscipline?.categories || {}).forEach(category => {
          Object.values(category.chapters || {}).forEach(chapterData => {
            const chapterId = chapterData.chapter.id;
            if (!filteredDocumentsByChapter[chapterId]) {
              filteredDocumentsByChapter[chapterId] = {
                chapter: chapterData.chapter,
                documents: []
              };
            }
            filteredDocumentsByChapter[chapterId].documents.push(...chapterData.documents);
          });
          if (category.withoutChapter) {
            filteredDocumentsWithoutChapter.push(...category.withoutChapter);
          }
        });
        
        Object.values(structure.withoutDiscipline?.withoutCategory?.chapters || {}).forEach(chapterData => {
          const chapterId = chapterData.chapter.id;
          if (!filteredDocumentsByChapter[chapterId]) {
            filteredDocumentsByChapter[chapterId] = {
              chapter: chapterData.chapter,
              documents: []
            };
          }
          filteredDocumentsByChapter[chapterId].documents.push(...chapterData.documents);
        });
        
        if (structure.withoutDiscipline?.withoutCategory?.withoutChapter) {
          filteredDocumentsWithoutChapter.push(...structure.withoutDiscipline.withoutCategory.withoutChapter);
        }
      };
      
      buildChapterMap(documentsByStructure);
    }
    
    // Count total documents for search results
    const totalResults = searchQuery.trim() 
      ? countDocumentsInStructure(structureToRender)
      : allDocuments.length;
    
    // Helper to render category section
    const renderCategorySection = (category, categoryData, disciplineId = null) => {
      const categoryChapters = Object.values(categoryData.chapters || {});
      const categoryWithoutChapter = categoryData.withoutChapter || [];
      const hasContent = categoryChapters.length > 0 || categoryWithoutChapter.length > 0;
      
      if (!hasContent) return null;

      const rootChapters = buildChapterForestFromBuckets(categoryChapters);

      const categoryKey = categoryExpansionKey(category, disciplineId);

      return (
        <div key={categoryKey} className="ml-6 border-l-2 border-green-300 pl-4">
          <div
            className="flex items-center justify-between py-3 cursor-pointer hover:bg-green-50 transition-colors duration-200 rounded-md px-2 -ml-2"
            onClick={() => toggleChapterExpansion(categoryKey)}
          >
            <div className="flex items-center space-x-2">
              <Squares2X2Icon className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">{category.name}</span>
              <span className="text-xs text-gray-400">
                ({categoryChapters.reduce((sum, ch) => sum + ch.documents.length, 0) + categoryWithoutChapter.length} dokumenter)
              </span>
            </div>
            {expandedChapters.has(categoryKey) ? (
              <ChevronUpIcon className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            )}
          </div>
          
          {expandedChapters.has(categoryKey) && (
            <div className="mt-2 space-y-2">
              {/* Render root chapters */}
              {rootChapters.map((chapter) => {
                const chapterDocs =
                  filteredDocumentsByChapter[chapter.id]?.documents ||
                  chapter.documents ||
                  [];
                return renderChapterWithDocuments(
                  chapter,
                  chapterDocs,
                  filteredDocumentsByChapter,
                  0
                );
              })}
              
              {/* Render documents without chapter in this category */}
              {categoryWithoutChapter.length > 0 && (
                <div className="bg-orange-50 rounded-lg border border-orange-200 p-3 mt-2">
                  <div className="text-xs text-orange-700 font-medium mb-2">
                    {t("Dokumenter uten kapittel")} ({categoryWithoutChapter.length})
                  </div>
                  <div className="space-y-1">
                    {categoryWithoutChapter.map((document) => (
                      <div
                        key={document.id}
                        onClick={() => handleDocumentClick(document)}
                        className="p-2 hover:bg-orange-100 cursor-pointer transition-colors duration-200 rounded-md"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center space-x-2">
                            <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-orange-600" />
                            <h4 className="text-sm font-medium text-gray-900">
                              {document.title}
                            </h4>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <DocReadStatusBadge doc={document} />
                            <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    };
    
    // Helper to render discipline section
    const renderDisciplineSection = (discipline, disciplineData) => {
      const disciplineCategories = Object.values(disciplineData.categories || {});
      const disciplineWithoutCategory = disciplineData.withoutCategory;
      const hasContent = disciplineCategories.length > 0 || 
        (disciplineWithoutCategory && (
          Object.keys(disciplineWithoutCategory.chapters || {}).length > 0 ||
          (disciplineWithoutCategory.withoutChapter || []).length > 0
        ));
      
      if (!hasContent) return null;
      
      const disciplineKey = `discipline_${discipline.id}`;
      
      return (
        <div key={disciplineKey} className="bg-white rounded-lg border border-gray-200 mb-4">
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
            onClick={() => toggleChapterExpansion(disciplineKey)}
          >
            <div className="flex items-center space-x-3">
              <BuildingOfficeIcon className="h-5 w-5 text-purple-600" />
              <div>
                <h3 className="font-medium text-gray-900">{discipline.name}</h3>
                <p className="text-sm text-gray-500">
                  {disciplineCategories.length} {disciplineCategories.length === 1 ? 'kategori' : 'kategorier'}
                </p>
              </div>
            </div>
            {expandedChapters.has(disciplineKey) ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          
          {expandedChapters.has(disciplineKey) && (
            <div className="border-t border-gray-200 p-4 space-y-4">
              {/* Render categories */}
              {disciplineCategories.map((categoryData) => 
                renderCategorySection(categoryData.category, categoryData, discipline.id)
              )}
              
              {/* Render documents without category in this discipline */}
              {disciplineWithoutCategory && (
                <div className="ml-6 border-l-2 border-yellow-300 pl-4">
                  <div className="text-xs text-yellow-700 font-medium mb-2">
                    {t("Dokumenter uten kategori")}
                  </div>
                  <div className="space-y-2">
                    {buildChapterForestFromBuckets(
                      Object.values(disciplineWithoutCategory.chapters || {})
                    ).map((chapter) => {
                      const chapterDocs =
                        filteredDocumentsByChapter[chapter.id]?.documents ||
                        chapter.documents ||
                        [];
                      return renderChapterWithDocuments(
                        chapter,
                        chapterDocs,
                        filteredDocumentsByChapter,
                        0
                      );
                    })}
                    {(disciplineWithoutCategory.withoutChapter || []).length > 0 && (
                      <div className="space-y-1">
                        {(disciplineWithoutCategory.withoutChapter || []).map((document) => (
                          <div
                            key={document.id}
                            onClick={() => handleDocumentClick(document)}
                            className="p-2 hover:bg-yellow-50 cursor-pointer transition-colors duration-200 rounded-md"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex min-w-0 items-center space-x-2">
                                <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-yellow-600" />
                                <h4 className="text-sm font-medium text-gray-900">
                                  {document.title}
                                </h4>
                              </div>
                              <div className="flex shrink-0 items-center gap-1.5">
                                <DocReadStatusBadge doc={document} />
                                <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    };
    
    return (
      <div className="space-y-4">
        {pendingAckCount > 0 ? (
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-600" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-950">
                {t("employee_handbook.pending_read_ack_title")}
              </p>
              <p className="mt-1 text-sm text-amber-900">
                {t("employee_handbook.pending_read_ack_body").replace(
                  "{count}",
                  String(pendingAckCount)
                )}
              </p>
            </div>
          </div>
        ) : null}
        {/* Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("Søk etter dokumenter...")}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <XCircleIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        
        {/* Search Results Info */}
        {searchQuery.trim() && (
          <div className="text-sm text-gray-600">
            {t("Fant {count} dokumenter").replace("{count}", totalResults)}
          </div>
        )}
        
        {/* Render full structure: Discipline -> Category -> Chapter -> Documents */}
        {(() => {
          const disciplines = Object.values(structureToRender.disciplines || {});
          const withoutDisciplineCategories = Object.values(structureToRender.withoutDiscipline?.categories || {});
          const withoutDisciplineWithoutCategory = structureToRender.withoutDiscipline?.withoutCategory;
          
          const hasAnyContent = disciplines.length > 0 || 
            withoutDisciplineCategories.length > 0 ||
            (withoutDisciplineWithoutCategory && (
              Object.keys(withoutDisciplineWithoutCategory.chapters || {}).length > 0 ||
              (withoutDisciplineWithoutCategory.withoutChapter || []).length > 0
            ));
          
          if (!hasAnyContent && searchQuery.trim()) {
            return (
              <div className="text-center py-8">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t("Ingen dokumenter funnet")}
                </h3>
                <p className="text-gray-500">
                  {t("Prøv å endre søkeordene dine")}
                </p>
              </div>
            );
          }
          
          return (
            <div className="space-y-4">
              {/* Render disciplines */}
              {disciplines.map((disciplineData) => 
                renderDisciplineSection(disciplineData.discipline, disciplineData)
              )}
              
              {/* Render categories without discipline */}
              {withoutDisciplineCategories.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 mb-4">
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-900">{t("Dokumenter uten bransje")}</h3>
                  </div>
                  <div className="p-4 space-y-4">
                    {withoutDisciplineCategories.map((categoryData) => 
                      renderCategorySection(categoryData.category, categoryData)
                    )}
                  </div>
                </div>
              )}
              
              {/* Render documents without discipline and category */}
              {withoutDisciplineWithoutCategory && (
                <div className="bg-white rounded-lg border border-gray-200 mb-4">
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-900">
                      {t("Dokumenter uten bransje og kategori")}
                    </h3>
                  </div>
                  <div className="p-4 space-y-2">
                    {buildChapterForestFromBuckets(
                      Object.values(withoutDisciplineWithoutCategory.chapters || {})
                    ).map((chapter) => {
                      const chapterDocs =
                        filteredDocumentsByChapter[chapter.id]?.documents ||
                        chapter.documents ||
                        [];
                      return renderChapterWithDocuments(
                        chapter,
                        chapterDocs,
                        filteredDocumentsByChapter,
                        0
                      );
                    })}
                    {(withoutDisciplineWithoutCategory.withoutChapter || []).length > 0 && (
                      <div className="bg-orange-50 rounded-lg border border-orange-200 p-3 mt-2">
                        <div className="text-xs text-orange-700 font-medium mb-2">
                          {t("Dokumenter uten kapittel")} ({(withoutDisciplineWithoutCategory.withoutChapter || []).length})
                        </div>
                        <div className="space-y-1">
                          {(withoutDisciplineWithoutCategory.withoutChapter || []).map((document) => (
                            <div
                              key={document.id}
                              onClick={() => handleDocumentClick(document)}
                              className="p-2 hover:bg-orange-100 cursor-pointer transition-colors duration-200 rounded-md"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center space-x-2">
                                  <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-orange-600" />
                                  <h4 className="text-sm font-medium text-gray-900">
                                    {document.title}
                                  </h4>
                                </div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  <DocReadStatusBadge doc={document} />
                                  <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  const renderEmployees = () => (
    <div className="space-y-4">
      {employees.map((employee) => (
        <div
          key={employee.id}
          onClick={() => handleEmployeeClick(employee)}
          className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors duration-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 rounded-full p-2">
                <UserGroupIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{employee.name}</h4>
                <div className="flex items-center space-x-4 mt-1">
                  {employee.department && (
                    <div className="flex items-center space-x-1">
                      <BuildingOfficeIcon className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {employee.department.displayTitle}
                      </span>
                    </div>
                  )}
                  {employee.mobile && (
                    <div className="flex items-center space-x-1">
                      <PhoneIcon className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {employee.mobile}
                      </span>
                    </div>
                  )}
                  {employee.email && (
                    <div className="flex items-center space-x-1">
                      <EnvelopeIcon className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {employee.email}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderHseHistory = () => (
    <div className="space-y-4">
      {hseEvents.length === 0 ? (
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("Ingen hendelser")}
          </h3>
          <p className="text-gray-500">
            {t("Ingen hendelser er rapportert ennå.")}
          </p>
        </div>
      ) : (
        hseEvents.map((event) => (
          <Link
            key={event.id}
            to={`/dashboard/hse/events/${event.id}`}
            className="bg-white rounded-lg p-4 border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors duration-200"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getHseEventTypeIcon(event.event_type)}
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{event.title}</h4>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-xs text-gray-500">
                      {new Date(event.event_date).toLocaleDateString()}
                    </span>
                    {getHseEventStatusBadge(event.status)}
                    {event.location && (
                      <div className="flex items-center space-x-1">
                        <BuildingOfficeIcon className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {event.location}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            </div>
          </Link>
        ))
      )}
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading />;
    }

    if (error) {
      return <ResourceError errors={[error]} />;
    }

    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "documents":
        return renderDocuments();
      case "employees":
        return renderEmployees();
      case "hse_history":
        return renderHseHistory();
      default:
        return renderOverview();
    }
  };

  return (
    <>
      <DashboardHeader
        subtitle=""
        title={t("Employee Handbook")}
        meta={[{ label: t("Employee Handbook"), icon: BookOpenIcon }]}
        mainButton={{
          label: t("Back"),
          action: () => navigate(-1),
          path: null,
          color: "",
          icon: ArrowLeftIcon,
        }}
      >
        <div className="mx-auto mt-8 max-w-4xl px-4 sm:px-6 lg:px-8">
          {/* Mobile-optimized tabs */}
          <div className="mb-6">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    activeTab === tab.id
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.id === "documents" && pendingAckCount > 0 ? (
                    <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold leading-snug text-white">
                      {pendingAckCount}
                    </span>
                  ) : null}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setToolsDialogOpen(true)}
                className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <WrenchScrewdriverIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{t("tools")}</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {renderContent()}
          </div>
        </div>
      </DashboardHeader>

      {/* Colleague Dialog */}
      <ColleagueDialog
        isOpen={colleagueDialogOpen}
        onClose={() => setColleagueDialogOpen(false)}
        colleague={selectedColleague}
      />

      <EmployeeToolsDialog
        open={toolsDialogOpen}
        onClose={() => setToolsDialogOpen(false)}
        userId={currentUser?.id}
      />

      {/* Document View Dialog */}
      <Transition.Root show={documentDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDocumentDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all w-full max-w-4xl rounded-lg">
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Dialog.Title className="text-lg font-semibold text-gray-900 truncate">
                          {selectedDocument?.title || t("Dokument")}
                        </Dialog.Title>
                        <div className="flex items-center space-x-2 mt-1">
                          {selectedDocument?.document_type_display && (
                            <span className="text-xs text-gray-500">
                              {selectedDocument.document_type_display}
                            </span>
                          )}
                          {selectedDocument?.category_display && (
                            <span className="text-xs text-gray-400">
                              • {selectedDocument.category_display}
                            </span>
                          )}
                          {selectedDocument?.chapter && (
                            <span className="text-xs text-gray-400">
                              • {selectedDocument.chapter.title}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setDocumentDialogOpen(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200 ml-4"
                    >
                      <XCircleIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-6 max-h-96 overflow-y-auto">
                    {loadingDocument ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-500">{t("Laster dokument...")}</p>
                      </div>
                    ) : documentContent?.content ? (
                      <div className="prose prose-sm max-w-none">
                        <HtmlTextFormatter string={documentContent.content} />
                      </div>
                    ) : documentContent?.description ? (
                      <div className="prose prose-sm max-w-none">
                        <p className="text-gray-700">{documentContent.description}</p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500">{t("Ingen innhold tilgjengelig")}</p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex flex-col gap-4 border-t border-gray-200 p-6 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <DocReadStatusBadge
                        doc={{
                          ...selectedDocument,
                          current_user_read_status:
                            documentContent?.current_user_read_status ??
                            selectedDocument?.current_user_read_status,
                        }}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Link
                        to={`/dashboard/hse-ks/documents/${selectedDocument?.id}`}
                        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        onClick={() => setDocumentDialogOpen(false)}
                      >
                        {t("employee_handbook.open_full_document")}
                      </Link>
                      {documentContent?.approved_version_id &&
                      documentContent?.current_user_read_status &&
                      !documentContent.current_user_read_status.acknowledged_read_understood &&
                      !documentContent.current_user_read_status.exempt_as_creator ? (
                        <button
                          type="button"
                          onClick={handleAcknowledgeRead}
                          disabled={ackSubmitting}
                          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
                        >
                          {ackSubmitting
                            ? t("hse.document.acknowledge_read_submitting")
                            : t("hse.document.acknowledge_read")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setDocumentDialogOpen(false)}
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        {t("Lukk")}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Org Charts Dialog */}
      {orgChartDialogOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("Velg organisasjonskart")}
                </h3>
                <button
                  onClick={() => setOrgChartDialogOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              </div>
              {orgCharts.length === 0 ? (
                <div className="text-center py-8">
                  <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">{t("Ingen godkjente organisasjonskart tilgjengelig")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {orgCharts.map((chart) => (
                    <Link
                      key={chart.id}
                      to={`/dashboard/quality/org-charts/${chart.id}`}
                      onClick={() => setOrgChartDialogOpen(false)}
                      className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{chart.title}</p>
                          {chart.description && (
                            <p className="text-sm text-gray-500 mt-1">{chart.description}</p>
                          )}
                          {chart.approved_revision && (
                            <div className="flex items-center space-x-2 mt-1">
                              <CheckCircleIcon className="h-4 w-4 text-green-500" />
                              <span className="text-xs text-gray-500">
                                {t("Revisjon")} {chart.approved_revision.revision_number}
                              </span>
                            </div>
                          )}
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
